import { useEffect, useRef, useState } from "react";
import { isSameDay, parseISO } from "date-fns";
import { Pin } from "lucide-react";
import { CATEGORIES, DAY_LABELS, catStyle } from "@/lib/constants";
import { blocksFor, fmtTime, hhmm, minutesOf, minutesOfDate, toDay, weekDays } from "@/lib/time";

const PX = 64;

function TaskBlock({ task, block, top, height, onClick }) {
  const c = catStyle(task.category);
  const pinned = task.pinned?.some((p) => p.start === block.start);
  const canDrag = task.status !== "done";
  const onDragStart = (e) => {
    const offset = Math.round(((e.clientY - e.currentTarget.getBoundingClientRect().top) / PX) * 60);
    e.dataTransfer.setData("text/plain", JSON.stringify({ id: task.id, from: block.start, offset }));
    e.dataTransfer.effectAllowed = "move";
  };
  return (
    <button
      onClick={() => onClick(task)}
      draggable={canDrag}
      onDragStart={onDragStart}
      data-testid={`calendar-task-block-${task.id}`}
      className={`absolute left-1 right-1 rounded-lg border-l-[3px] px-2 py-1 text-left overflow-hidden hover:-translate-y-[1px] hover:shadow-md transition-[transform,box-shadow] duration-200 fade-up ${canDrag ? "cursor-grab active:cursor-grabbing" : ""}`}
      style={{ top, height, background: c.bg, borderColor: c.border, color: c.text, opacity: task.status === "done" ? 0.45 : 1 }}
    >
      <p className={`text-xs font-semibold leading-tight truncate pr-3 ${task.status === "done" ? "line-through" : ""}`}>{task.title}</p>
      {height > 34 && <p className="text-[11px] opacity-80 truncate">{task.client_name || task.category}</p>}
      {height > 50 && <p className="text-[10px] opacity-60">{fmtTime(block.start)}–{fmtTime(block.end)}</p>}
      {pinned && <Pin size={11} className="absolute top-1 right-1 opacity-80" data-testid={`calendar-pinned-${task.id}`} />}
      {task.at_risk && !pinned && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-500" />}
    </button>
  );
}

function EventBlock({ ev, top, height, onClick }) {
  return (
    <button
      onClick={() => onClick(ev)}
      data-testid={`calendar-event-block-${ev.id}`}
      className="absolute left-1 right-1 rounded-lg px-2 py-1 text-left overflow-hidden bg-[repeating-linear-gradient(135deg,#F5F2EB,#F5F2EB_6px,#EDE8DD_6px,#EDE8DD_12px)] border border-[#103080]/10 text-[#5A5141] hover:shadow-md transition-shadow duration-200"
      style={{ top, height }}
    >
      <p className="text-xs font-semibold truncate">{ev.title}</p>
      {height > 34 && <p className="text-[10px] opacity-70">{fmtTime(ev.start)}–{fmtTime(ev.end)}</p>}
    </button>
  );
}

function DayColumn({ day, startMin, endMin, windows, tasks, events, onTaskClick, onEventClick, onMoveBlock }) {
  const [over, setOver] = useState(false);
  const onDrop = (e) => {
    e.preventDefault();
    setOver(false);
    let data;
    try {
      data = JSON.parse(e.dataTransfer.getData("text/plain"));
    } catch {
      return;
    }
    const y = e.clientY - e.currentTarget.getBoundingClientRect().top;
    const min = Math.round((startMin + (y / PX) * 60 - data.offset) / 15) * 15;
    const clamped = Math.max(startMin, Math.min(endMin - 15, min));
    onMoveBlock(data.id, data.from, `${toDay(day)}T${hhmm(clamped)}`);
  };
  const pos = (s, e) => {
    const a = Math.max(minutesOfDate(s), startMin);
    const b = Math.min(minutesOfDate(e), endMin);
    return { top: ((a - startMin) / 60) * PX, height: Math.max(((b - a) / 60) * PX - 2, 18) };
  };
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const isToday = isSameDay(day, now);
  return (
    <div className={`relative border-l border-[#103080]/10 bg-[#103080]/[0.04] ${over ? "ring-2 ring-inset ring-[#0090D0]/40" : ""}`} style={{ height: ((endMin - startMin) / 60) * PX }} data-testid={`calendar-day-${toDay(day)}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={onDrop}>
      {windows.map((w) => (
        <div key={w.start} className="absolute inset-x-0 bg-white/90" data-testid="calendar-work-window"
          style={{ top: ((Math.max(minutesOf(w.start), startMin) - startMin) / 60) * PX, height: ((Math.min(minutesOf(w.end), endMin) - Math.max(minutesOf(w.start), startMin)) / 60) * PX }} />
      ))}
      {events.filter((e) => isSameDay(parseISO(e.start), day)).map((ev) => (
        <EventBlock key={ev.id} ev={ev} onClick={onEventClick} {...pos(ev.start, ev.end)} />
      ))}
      {tasks.flatMap((t) => t.blocks.filter((b) => isSameDay(parseISO(b.start), day)).map((b) => (
        <TaskBlock key={`${t.id}-${b.start}`} task={t} block={b} onClick={onTaskClick} {...pos(b.start, b.end)} />
      )))}
      {isToday && nowMin >= startMin && nowMin <= endMin && (
        <div className="absolute inset-x-0 z-10 h-[2px] bg-[#0090D0]" style={{ top: ((nowMin - startMin) / 60) * PX }} data-testid="calendar-now-line">
          <span className="absolute -left-1 -top-[3px] w-2 h-2 rounded-full bg-[#0090D0]" />
        </div>
      )}
    </div>
  );
}

export default function WeekCalendar({ weekStart, settings, tasks, events, overrides, onTaskClick, onEventClick, onMoveBlock }) {
  const scroller = useRef(null);
  const days = weekDays(weekStart).filter((d) => blocksFor(d, settings, overrides).length > 0 || d.getDay() % 6 !== 0);
  const all = days.flatMap((d) => blocksFor(d, settings, overrides));
  const startMin = 60;
  const endMin = 24 * 60;
  const firstWork = Math.min(...all.map((b) => minutesOf(b.start)), 9 * 60);
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = Math.max(0, ((firstWork - 30 - startMin) / 60) * PX);
  }, [firstWork, weekStart]);
  const hours = Array.from({ length: (endMin - startMin) / 60 }, (_, i) => startMin / 60 + i);
  const cols = { gridTemplateColumns: `56px repeat(${days.length}, minmax(120px, 1fr))` };
  return (
    <section className="rounded-2xl border border-[#103080]/10 bg-white/70 ambient overflow-hidden" data-testid="week-calendar">
      <div className="overflow-x-auto thin-scroll">
        <div className="min-w-[700px]">
          <div className="grid border-b border-[#103080]/10 bg-[#F5F2EB]/60" style={cols}>
            <div />
            {days.map((d) => (
              <div key={d.toISOString()} className={`px-3 py-3 border-l border-[#103080]/10 ${isSameDay(d, new Date()) ? "text-[#0090D0]" : "text-[#103080]"}`}>
                <p className="text-[11px] uppercase tracking-[0.2em] font-semibold opacity-70">{DAY_LABELS[(d.getDay() + 6) % 7]}</p>
                <p className="font-serif-brand text-2xl font-semibold">{d.getDate()}</p>
              </div>
            ))}
          </div>
          <div ref={scroller} className="max-h-[640px] overflow-y-auto thin-scroll" data-testid="calendar-scroll">
          <div className="grid relative" style={cols}>
            <div className="relative">
              {hours.map((h) => (
                <div key={h} className="text-[11px] text-[#0F172A]/40 pr-2 text-right" style={{ height: PX }}>{String(h).padStart(2, "0")}:00</div>
              ))}
            </div>
            <div className="absolute inset-0 pointer-events-none" style={{ left: 56 }}>
              {hours.map((h, i) => <div key={h} className="absolute inset-x-0 border-t border-[#103080]/[0.06]" style={{ top: i * PX }} />)}
            </div>
            {days.map((d) => (
              <DayColumn key={d.toISOString()} day={d} startMin={startMin} endMin={endMin} windows={blocksFor(d, settings, overrides)}
                tasks={tasks} events={events} onTaskClick={onTaskClick} onEventClick={onEventClick} onMoveBlock={onMoveBlock} />
            ))}
          </div>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-2 px-4 py-3 border-t border-[#103080]/10 bg-[#F5F2EB]/40" data-testid="calendar-legend">
        {Object.entries(CATEGORIES).map(([name, c]) => (
          <span key={name} className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#0F172A]/70">
            <span className="w-3 h-3 rounded-[4px] border-l-[3px]" style={{ background: c.bg, borderColor: c.border }} />{name}
          </span>
        ))}
      </div>
    </section>
  );
}
